// =====================================================
// HOOK: useFiles
// Description: Hook pour gérer les fichiers des entités (contacts, équipages)
// =====================================================

import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { EntityFile, EntityType, UploadFileInput } from '../types/file';

export function useFiles() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Récupérer les fichiers d'une entité
   */
  const getFilesByEntity = useCallback(
    async (entityType: EntityType, entityId: string): Promise<EntityFile[]> => {
      setLoading(true);
      setError(null);

      try {
        const { data, error: fetchError } = await supabase
          .from('files')
          .select('*')
          .eq('entity_type', entityType)
          .eq('entity_id', entityId)
          .order('created_at', { ascending: false });

        if (fetchError) {
          console.error('Error fetching files:', fetchError);
          setError(fetchError.message);
          return [];
        }

        return data || [];
      } catch (err) {
        console.error('Error fetching files:', err);
        setError('Une erreur est survenue lors de la récupération des fichiers');
        return [];
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Upload un fichier et créer l'entrée en base de données
   */
  const uploadFile = useCallback(async (input: UploadFileInput): Promise<EntityFile | null> => {
    setLoading(true);
    setError(null);

    try {
      const { entity_type, entity_id, file, name } = input;

      // Générer un nom de fichier unique
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${entity_type}/${entity_id}/${timestamp}-${sanitizedFileName}`;

      // Upload le fichier dans le storage
      const { error: uploadError } = await supabase.storage
        .from('entity-files')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        console.error('Error uploading file:', uploadError);
        setError(uploadError.message);
        return null;
      }

      // Créer l'entrée en base de données
      const { data: user } = await supabase.auth.getUser();

      const { data, error: insertError } = await supabase
        .from('files')
        .insert({
          entity_type,
          entity_id,
          name,
          file_path: filePath,
          file_size: file.size,
          mime_type: file.type || 'application/octet-stream',
          uploaded_by: user.user?.id || null,
        })
        .select()
        .single();

      if (insertError) {
        console.error('Error creating file record:', insertError);
        // Essayer de supprimer le fichier uploadé en cas d'erreur
        await supabase.storage.from('entity-files').remove([filePath]);
        setError(insertError.message);
        return null;
      }

      return data;
    } catch (err) {
      console.error('Error uploading file:', err);
      setError("Une erreur est survenue lors de l'upload du fichier");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Supprimer un fichier
   */
  const deleteFile = useCallback(async (file: EntityFile): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      // Supprimer le fichier du storage
      const { error: storageError } = await supabase.storage
        .from('entity-files')
        .remove([file.file_path]);

      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
        setError(storageError.message);
        return false;
      }

      // Supprimer l'entrée de la base de données
      const { error: deleteError } = await supabase.from('files').delete().eq('id', file.id);

      if (deleteError) {
        console.error('Error deleting file record:', deleteError);
        setError(deleteError.message);
        return false;
      }

      return true;
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Une erreur est survenue lors de la suppression du fichier');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Obtenir l'URL publique d'un fichier
   */
  const getFileUrl = useCallback((filePath: string): string => {
    const { data } = supabase.storage.from('entity-files').getPublicUrl(filePath);
    return data.publicUrl;
  }, []);

  return {
    loading,
    error,
    getFilesByEntity,
    uploadFile,
    deleteFile,
    getFileUrl,
  };
}
